import {
  collection,
  getDocs,
  getFirestore
} from "firebase/firestore";
import app, {getCloudFunctionUrl} from "../firebase";

const UPDATE_USER_URL = getCloudFunctionUrl("updateUserProfile");

function validationError(message, code) {
  const error = new Error(message);
  error.code = code;
  return error;
}

function validateUpdatePayload(payload) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw validationError("A valid user update payload is required.", "invalid-payload");
  }

  const { collectionName, documentId, accountUid, data } = payload;
  if (!["users", "students"].includes(collectionName) || typeof documentId !== "string" || !documentId.trim()) {
    throw validationError("A valid user record is required.", "invalid-payload");
  }
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    throw validationError("User update data is required.", "invalid-payload");
  }
  if (accountUid !== undefined && typeof accountUid !== "string") {
    throw validationError("A valid account identifier is required.", "invalid-payload");
  }

  const email = typeof data.email === "string" ? data.email.trim() : "";
  if (!email) {
    throw validationError("Email is required.", "invalid-email");
  }
  if (email.length < 3 || email.length > 25) {
    throw validationError("Email must be between 3 and 25 characters.", "invalid-email");
  }
  if (!email.includes("@") || !email.toLowerCase().includes(".com")) {
    throw validationError('Email must contain "@" and ".com".', "invalid-email");
  }

  return { collectionName, documentId, accountUid: accountUid || "", data, email };
}

export async function updateManagedUser(payload) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw validationError("A valid user update payload is required.", "invalid-payload");
  }
  const {caller, collectionName, documentId, accountUid, data} = payload;
  if (!caller) throw validationError("Sign in again before editing users.", "unauthenticated");
  const validated = validateUpdatePayload({collectionName, documentId, accountUid, data});
  const db = getFirestore(app);
  const [usersSnapshot, studentsSnapshot] = await Promise.all([
    getDocs(collection(db, "users")),
    getDocs(collection(db, "students"))
  ]);
  const normalizedEmail = validated.email.toLowerCase();
  const hasDuplicate = [
    ["users", usersSnapshot],
    ["students", studentsSnapshot]
  ].some(([candidateCollection, snapshot]) => snapshot.docs.some(candidate => {
    const candidateData = candidate.data();
    const isCurrentRecord = candidateCollection === collectionName && candidate.id === documentId;
    const isLinkedAccount = accountUid && (
      candidate.id === accountUid ||
      candidateData.uid === accountUid
    );
    const candidateEmail = typeof candidateData.email === "string"
      ? candidateData.email.trim().toLowerCase()
      : "";
    return !isCurrentRecord && !isLinkedAccount && candidateEmail === normalizedEmail;
  }));

  if (hasDuplicate) {
    throw validationError("This email is already used by another account.", "email-already-exists");
  }

  const token = await caller.getIdToken(true);
  const response = await fetch(UPDATE_USER_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({
      collectionName,
      documentId,
      data: {...data, email: validated.email}
    })
  });
  const result = await response.json().catch(() => null);
  if (!response.ok || result?.ok !== true) {
    const error = new Error(result?.error?.message || "The user update could not be completed.");
    error.code = result?.error?.code || "user_update_failed";
    throw error;
  }
}
